<template>
  <v-row class="tax-gyp-patrimoniales-row">
    <v-col cols="12" md="12" lg="5" class="px-5 mt-0 pt-0">
      <TaxTitle
        title="Tipo de elemento patrimonial. Clave"
        :boxNumber="boxes.clave.number"
        color="primary"
      />
      <v-simple-table fixed-header class="tax-gyp-patrimoniales-table mt-4">
        <template v-slot:default>
          <tbody>
            <template v-if="!boxes.fechasValores.data">
              <tr class="v-data-table__empty-wrapper">
                <td :colspan="2">
                  No hay G/P de {{ typeText }} durante el año fiscal seleccionado
                </td>
              </tr>
            </template>
            <template v-else>
              <tr>
                <td>
                  <span>
                    Tipo de elemento patrimonial. Clave
                  </span>
                </td>
                <td class="text-right">
                  <div class="items-flex justify-end">
                    <div class="clave-number">
                      <span>
                        <v-chip>
                          <strong>
                            {{ boxes.clave.data.clave }}
                          </strong>
                        </v-chip>
                      </span>
                    </div>
                    <div class="ml-2">
                      <InfoIcon
                        :size="17"
                        :text="`Selecciona el número ${boxes.clave.data.clave}: Otros elementos patrimoniales no afectos a actividades económicas`"
                      />
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
    </v-col>
    <v-col cols="12" md="12" lg="7" class="px-4 mt-0 pt-0">
      <TaxTitle
        title="Fechas y valores de transmisión y de adquisición"
        :boxNumber="boxes.fechasValores.number"
        color="primary"
      />
      <template v-if="boxes.fechasValores.data && boxes.fechasValores.somePendingFifo">
        <v-alert color="softWarning" icon="mdi-alert" class="mt-3">
          Ciertas posiciones FX están pendientes de ser calculadas. Hasta entonces, los datos
          mostrados para valores de transmisión y adquisición pueden no ser correctos.
        </v-alert>
      </template>
      <template
        v-if="
          boxes.fechasValores.data &&
          !boxes.fechasValores.somePendingFifo &&
          boxes.fechasValores.data.valorTransmision &&
          boxes.fechasValores.data.valorAdquisicion &&
          boxes.fechasValores.data.valorTransmision === boxes.fechasValores.data.valorAdquisicion
        "
      >
        <v-alert color="softInfo" icon="mdi-information" class="mt-3">
          Sólo es necesario que rellenes este apartado en caso de que hayas invertido en un bróker
          multidivisa (por ejemplo IBKR)
        </v-alert>
      </template>
      <v-simple-table fixed-header class="tax-gyp-patrimoniales-table mt-4">
        <template v-slot:default>
          <tbody>
            <template v-if="!boxes.fechasValores.data">
              <tr class="v-data-table__empty-wrapper">
                <td :colspan="2">
                  No hay G/P de {{ typeText }} durante el año fiscal seleccionado
                </td>
              </tr>
            </template>
            <template v-else>
              <tr>
                <td>
                  <span>
                    Fecha de transmisión
                    <InfoIcon
                      :size="15"
                      :text="`Fecha de la última venta del año en movimientos de ${typeText}.`"
                    />
                  </span>
                </td>
                <td class="text-right">
                  {{ getDateUserFormat(boxes.fechasValores.data.fechaTransmision) }}
                </td>
              </tr>
              <tr>
                <td>
                  <span>
                    Fecha de adquisición
                    <InfoIcon
                      :size="15"
                      :text="`Fecha de la primera compra vendida en la primera venta del año en movimientos de ${typeText}.`"
                    />
                  </span>
                </td>
                <td class="text-right">
                  {{ getDateUserFormat(boxes.fechasValores.data.fechaAdquisicion) }}
                </td>
              </tr>
              <tr
                :class="{
                  'row-advise': boxes.fechasValores.someIncomplete,
                }"
              >
                <td class="items-flex">
                  <span>
                    Valor de transmisión
                    <InfoIcon
                      :size="15"
                      :text="`Cantidad en EUR total obtenida en la venta de los movimientos de ${typeText}.`"
                    />
                  </span>
                  <template v-if="boxes.fechasValores.someIncomplete">
                    <div class="ml-3">
                      <WarningIcon
                        v-if="type === 'fx'"
                        :text="$t(`views.tax.gypPatrimoniales.warning.fx`)"
                      />
                      <WarningIcon
                        v-else
                        :text="$t(`views.tax.gypPatrimoniales.warning.default`)"
                      />
                    </div>
                  </template>
                </td>
                <td class="text-right">
                  <currency
                    v-if="boxes.fechasValores.data.valorTransmision"
                    :value="boxes.fechasValores.data.valorTransmision"
                    :currency="user.baseCurrency"
                  />
                  <span v-else></span>
                </td>
              </tr>
              <tr
                :class="{
                  'row-advise': boxes.fechasValores.someIncomplete,
                }"
              >
                <td class="items-flex">
                  <span>
                    Valor de adquisición
                    <InfoIcon
                      :size="15"
                      :text="`Cantidad en EUR total empleada en la venta de los movimientos de ${typeText}.`"
                    />
                  </span>
                  <template v-if="boxes.fechasValores.someIncomplete">
                    <div class="ml-3">
                      <WarningIcon
                        v-if="type === 'fx'"
                        :text="$t(`views.tax.gypPatrimoniales.warning.fx`)"
                      />
                      <WarningIcon
                        v-else
                        :text="$t(`views.tax.gypPatrimoniales.warning.default`)"
                      />
                    </div>
                  </template>
                </td>
                <td class="text-right">
                  <currency
                    v-if="boxes.fechasValores.data.valorAdquisicion"
                    :value="boxes.fechasValores.data.valorAdquisicion"
                    :currency="user.baseCurrency"
                    :hideDataNoPlanUser="hideDataNoPlanUser"
                  />
                  <span v-else></span>
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
    </v-col>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import Currency from '@/views/components/common/Currency'
import TaxTitle from '@/views/components/tax/taxDocs/renta/TaxTitle'
import InfoIcon from '@/views/components/common/InfoIcon'
import WarningIcon from '@/views/components/common/WarningIcon'
import { getDateUserFormat } from '@/lib/utils/date.js'

export default {
  components: { TaxTitle, InfoIcon, WarningIcon, Currency },
  props: {
    type: {
      type: String,
      required: true,
    },
    boxes: {
      type: Object,
      default: () => {},
    },
    hideDataNoPlanUser: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(['user']),
    typeText() {
      if (this.type === 'crypto') return 'cripto'
      else if (this.type === 'cfd') return 'materias primas'
      else if (this.type === 'fx') return 'FX'
      else return this.type
    },
  },
  methods: {
    getDateUserFormat,
  },
}
</script>

<style lang="scss">
.tax-gyp-patrimoniales-row {
  .tax-gyp-patrimoniales-table {
    .items-flex {
      display: flex;
      align-items: center;
      &.justify-end {
        justify-content: flex-end;
      }
    }
    .clave-number {
      .v-chip .v-chip__content {
        font-size: 14px;
      }
    }
  }
}
</style>
